import Tags from "@components/job_post/tags.tsx";
import Divider from "@sprout/divider.tsx";
import CompanyLogo from "@components/company_logo.tsx";
import { SecondaryButton } from "@sprout/buttons";
import { IconButton } from "@sprout/icon_button.tsx";
import ReminderIcon from "@sprout/icons/reminder.tsx";
import Tooltip from "@sprout/tooltip.tsx";
import useDevice from "@hooks/use_device.ts";
import "./application_form_header.scss";
import useTranslation from "@hooks/use_translation.ts";
import { useTimeAgo, composeStatusLabel } from "@utils/time_ago";
import type { JobPost, SearchSpecificFields } from "@/types/jben/job_post.ts";

interface JobHeaderProps {
  title: string;
  companyName: string;
  logoUrl: string;
  publicUrl?: string;
  locations?: string[];
  workType?: string | null;
  payRange?: string;
  firstPublished: string;
  onCreateAlert?: () => void;
}

export const JobHeader = ({
  title,
  companyName,
  logoUrl,
  publicUrl,
  locations,
  workType,
  payRange,
  firstPublished,
  onCreateAlert,
}: JobHeaderProps) => {
  const { t } = useTranslation("jobSearch");
  const device = useDevice();
  const timeSincePublished = composeStatusLabel(t("posted"), useTimeAgo(firstPublished));

  return (
    <div className={"flex flex-col gap-6 pb-6"}>
      <div className={"flex flex-row justify-between gap-4"}>
        <div className={"flex flex-col gap-4 md:flex-row"}>
          <CompanyLogo companyName={companyName} logoUrl={logoUrl} size={"large"} />
          <div className={"flex flex-col justify-center items-start gap-2"}>
            <a href={publicUrl} target={"_blank"} rel="noreferrer">
              <h2 className={"application-form-header--title"}>{title}</h2>
            </a>
            <h3 className={"application-form-header--company-name"}>{companyName}</h3>
          </div>
        </div>
        {onCreateAlert && (
          <div className="shrink-0">
            {device.isMobile ? (
              <Tooltip text={t("createJobAlert")} asChild>
                <IconButton
                  icon={ReminderIcon}
                  label={t("createJobAlert")}
                  size="lg"
                  onClick={onCreateAlert}
                  data-provides="create-job-alert"
                />
              </Tooltip>
            ) : (
              <SecondaryButton
                size="medium"
                onClick={onCreateAlert}
                data-provides="create-job-alert"
              >
                <span className="flex items-center gap-1">
                  <ReminderIcon size="sm" color="green" ariaHidden />
                  {t("createJobAlert")}
                </span>
              </SecondaryButton>
            )}
          </div>
        )}
      </div>
      <div className={"flex flex-row justify-start"}>
        <Tags
          locations={locations}
          workType={workType}
          payRange={payRange}
          secondaryText={timeSincePublished}
        />
      </div>
      <Divider />
    </div>
  );
};

interface Props {
  jobPost: JobPost & SearchSpecificFields;
  onCreateAlert?: () => void;
}

const ApplicationFormHeader = ({ jobPost, onCreateAlert }: Props) => (
  <JobHeader
    title={jobPost.title}
    companyName={jobPost.company_name}
    logoUrl={jobPost.logo_url}
    publicUrl={jobPost.public_url}
    locations={jobPost.locations}
    workType={jobPost.work_type}
    payRange={jobPost.pay_range_string ?? undefined}
    firstPublished={jobPost.first_published}
    onCreateAlert={onCreateAlert}
  />
);

export default ApplicationFormHeader;
